/* ═══════════════════════════════════
   TACTICAL DOSSIER GRID - JavaScript v3
   assets/tac-dossier.js
   Scroll animations, counters, read-more, parallax, magnetic hover
   ═══════════════════════════════════ */
(function() {
  var sections = document.querySelectorAll('[data-section-type="tac-dossier-grid"]');
  if (!sections.length) return;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Track all cleanup functions per section id ── */
  window.__tacDossierCleanup = window.__tacDossierCleanup || {};

  sections.forEach(function(section) {
    /* Prevent double-init */
    if (section.dataset.tacDossierInit) return;
    section.dataset.tacDossierInit = '1';

    var sectionId = section.dataset.sectionId || section.id || ('tac-d-' + Math.random().toString(36).substr(2, 6));
    var observers = [];
    var resizeTimer = null;

    /* ── Scroll-triggered animations ── */
    if (!reducedMotion && 'IntersectionObserver' in window) {
      var animObserver = new IntersectionObserver(function(entries) {
        var visibleEntries = entries.filter(function(e) { return e.isIntersecting; });
        visibleEntries.forEach(function(entry, i) {
          entry.target.style.animationDelay = (i * 0.07) + 's';
          entry.target.classList.add('tac-d-visible');
          animObserver.unobserve(entry.target);
        });
      }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

      section.querySelectorAll('.tac-d-card, .tac-d-banner, .tac-d-summary__item').forEach(function(el) {
        animObserver.observe(el);
      });
      observers.push(animObserver);
    } else {
      section.querySelectorAll('.tac-d-card, .tac-d-banner, .tac-d-summary__item').forEach(function(el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
    }

    /* ── Summary bar counter animation ── */
    if (!reducedMotion && 'IntersectionObserver' in window) {
      var counterObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          counterObserver.unobserve(el);

          var raw = el.textContent.trim();
          var match = raw.match(/^([0-9,]+(?:\.\d+)?)\s*(.*)$/);
          if (!match) return;

          var numStr = match[1].replace(/,/g, '');
          var target = parseFloat(numStr);
          var suffix = match[2] || '';
          var hasCommas = match[1].indexOf(',') !== -1;
          var hasDecimal = numStr.indexOf('.') !== -1;
          var decimalPlaces = hasDecimal ? numStr.split('.')[1].length : 0;

          if (isNaN(target) || target === 0) return;

          var duration = 1200;
          var startTime = null;

          function formatNumber(n) {
            var formatted = hasDecimal ? n.toFixed(decimalPlaces) : Math.floor(n).toString();
            if (hasCommas) {
              var parts = formatted.split('.');
              parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
              formatted = parts.join('.');
            }
            return formatted + suffix;
          }

          function step(timestamp) {
            if (!startTime) startTime = timestamp;
            var progress = Math.min((timestamp - startTime) / duration, 1);
            var eased = 1 - Math.pow(1 - progress, 3);
            el.textContent = formatNumber(eased * target);
            if (progress < 1) {
              requestAnimationFrame(step);
            } else {
              el.textContent = raw;
            }
          }

          el.textContent = formatNumber(0);
          requestAnimationFrame(step);
        });
      }, { threshold: 0.3 });

      section.querySelectorAll('[data-tac-countup]').forEach(function(el) {
        counterObserver.observe(el);
      });
      observers.push(counterObserver);
    }

    /* ── Read-more toggle for mobile taglines ── */
    section.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-tac-readmore]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var tagline = btn.closest('.tac-d-card__tagline');
      if (!tagline) return;
      var expanded = tagline.classList.toggle('is-expanded');
      btn.textContent = expanded ? 'Read less' : 'Read more';
    });

    section.addEventListener('keydown', function(e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var btn = e.target.closest('[data-tac-readmore]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var tagline = btn.closest('.tac-d-card__tagline');
      if (!tagline) return;
      var expanded = tagline.classList.toggle('is-expanded');
      btn.textContent = expanded ? 'Read less' : 'Read more';
    });

    /* Hide read-more buttons when text isn't actually clamped */
    function checkClamps() {
      if (window.innerWidth > 749) return;
      var texts = section.querySelectorAll('.tac-d-card__tagline-text');
      texts.forEach(function(txt) {
        var btn = txt.parentElement.querySelector('[data-tac-readmore]');
        if (!btn) return;
        var tagline = txt.parentElement;
        if (tagline.classList.contains('is-expanded')) return;
        btn.style.display = txt.scrollHeight > txt.clientHeight + 2 ? '' : 'none';
      });
    }

    function debouncedCheckClamps() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkClamps, 150);
    }

    checkClamps();
    requestAnimationFrame(function() {
      checkClamps();
    });
    window.addEventListener('resize', debouncedCheckClamps);
    if (document.fonts) document.fonts.ready.then(checkClamps);

    /* ── Image parallax on scroll ──
       Only runs getBoundingClientRect on images currently in/near viewport.
       IntersectionObserver tracks which images are visible; scroll handler
       only touches those. Prevents forced reflow on 15+ offscreen images.
       Disabled on mobile (< 750px) to prevent GPU memory exhaustion from
       too many compositor layers on memory-constrained devices. */
    var parallaxRaf = null;
    var visibleImages = new Set();
    var isMobile = window.innerWidth < 750;

    if (!reducedMotion && !isMobile && 'IntersectionObserver' in window) {
      var parallaxObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            visibleImages.add(entry.target);
          } else {
            visibleImages.delete(entry.target);
            /* Reset transform when leaving viewport so CSS hover still works */
            entry.target.style.transform = '';
          }
        });
      }, { rootMargin: '100px 0px' });

      section.querySelectorAll('.tac-d-card__img:not(.tac-d-card__img--placeholder)').forEach(function(img) {
        parallaxObserver.observe(img);
      });
      observers.push(parallaxObserver);
    }

    function tacParallax() {
      visibleImages.forEach(function(img) {
        var rect = img.getBoundingClientRect();
        var offset = rect.top * -0.03;
        img.style.transform = 'translateY(' + offset + 'px) scale(1.04)';
      });
      parallaxRaf = null;
    }

    function onScrollParallax() {
      if (parallaxRaf || !visibleImages.size) return;
      parallaxRaf = requestAnimationFrame(tacParallax);
    }

    if (!reducedMotion && !isMobile) {
      window.addEventListener('scroll', onScrollParallax, { passive: true });
    }

    /* ── Magnetic hover for cards (desktop only - 3D transforms create compositor layers) ── */
    if (!reducedMotion && !isMobile) {
      var cards = section.querySelectorAll('.tac-d-card');
      cards.forEach(function(card) {
        card.addEventListener('mousemove', function(e) {
          var rect = card.getBoundingClientRect();
          var x = e.clientX - rect.left;
          var y = e.clientY - rect.top;
          var centerX = rect.width / 2;
          var centerY = rect.height / 2;
          var rotateX = ((y - centerY) / centerY) * -3;
          var rotateY = ((x - centerX) / centerX) * 3;
          card.style.transform = 'perspective(800px) rotateX(' + rotateX + 'deg) rotateY(' + rotateY + 'deg)';
        });

        card.addEventListener('mouseleave', function() {
          card.style.transform = '';
        });
      });
    }

    /* ── Cleanup function ── */
    function cleanup() {
      observers.forEach(function(obs) { obs.disconnect(); });
      observers = [];
      window.removeEventListener('resize', debouncedCheckClamps);
      window.removeEventListener('scroll', onScrollParallax);
      clearTimeout(resizeTimer);
      if (parallaxRaf) {
        cancelAnimationFrame(parallaxRaf);
        parallaxRaf = null;
      }
    }

    window.__tacDossierCleanup[sectionId] = cleanup;

  }); /* end sections.forEach */

  /* ── Shopify section lifecycle cleanup ── */
  document.addEventListener('shopify:section:unload', function(e) {
    var el = e.target;
    if (!el || !el.querySelector || !el.querySelector('[data-section-type="tac-dossier-grid"]')) return;
    var grid = el.querySelector('[data-section-type="tac-dossier-grid"]');
    if (!grid) return;
    var sid = grid.dataset.sectionId || grid.id || '';
    if (sid && window.__tacDossierCleanup[sid]) {
      window.__tacDossierCleanup[sid]();
      delete window.__tacDossierCleanup[sid];
    }
  });
})();
